import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ConsentSection = ({ onAccept, onDecline }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [parentName, setParentName] = useState('');

  const handleAccept = () => {
    if (acknowledged && parentName?.trim()) {
      onAccept();
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-card border border-border rounded-lg p-8 shadow-sm">
        <div className="flex items-center mb-6">
          <div className="bg-primary/10 p-4 rounded-full mr-4">
            <Icon name="FileText" className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-text-primary">
            Parental Responsibility Acknowledgement
          </h1>
        </div>

        <div className="space-y-6 mb-8">
          <div className="bg-surface rounded-lg p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Your Responsibilities</h2>
            <ul className="space-y-3 text-text-secondary">
              <li className="flex items-start">
                <Icon name="Check" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                <span>I am the parent or legal guardian of the child whose data I will enter</span>
              </li>
              <li className="flex items-start">
                <Icon name="Check" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                <span>I understand this app does not provide medical advice or diagnosis</span>
              </li>
              <li className="flex items-start">
                <Icon name="Check" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                <span>I will consult healthcare professionals for any medical concerns</span>
              </li>
              <li className="flex items-start">
                <Icon name="Check" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                <span>I understand that maturity estimates are shown with uncertainty and are not definitive</span>
              </li>
              <li className="flex items-start">
                <Icon name="Check" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                <span>I will take accurate measurements following the guidance provided</span>
              </li>
              <li className="flex items-start">
                <Icon name="Check" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                <span>I understand that small errors can create false growth spurts in the data</span>
              </li>
            </ul>
          </div>

          <div className="bg-warning/10 border border-warning rounded-lg p-6">
            <div className="flex items-start">
              <Icon name="AlertTriangle" className="w-6 h-6 text-warning mr-3 mt-1 flex-shrink-0" />
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-text-primary mb-3">Safety Caveats - Please Read Carefully</h3>
                
                <div className="text-sm text-text-secondary space-y-2">
                  <p className="leading-relaxed">
                    <span className="font-medium text-text-primary">Purpose and Limits:</span> This app is for education and self monitoring. It does not provide medical advice, it does not diagnose conditions, and it cannot predict injuries. If you are worried about your child's health, growth, pain, or wellbeing, contact a qualified healthcare professional.
                  </p>
                  
                  <p className="leading-relaxed">
                    <span className="font-medium text-text-primary">Uncertainty:</span> Peak height velocity is estimated from patterns in repeated height measurements. Estimates can be wrong, especially when measurements are infrequent or inconsistent. The app will show a confidence grade, and it may refuse to estimate peak height velocity when data quality is insufficient.
                  </p>
                  
                  <p className="leading-relaxed">
                    <span className="font-medium text-text-primary">Measurement Error:</span> Small errors can create false growth spurts in the data. Measure height in the same way each time, ideally at the same time of day, and use two back to back measures.
                  </p>
                  
                  <p className="leading-relaxed">
                    <span className="font-medium text-text-primary">Training Advice Boundaries:</span> The guidance provided is general. It is not a personalised training plan. Children develop at different rates, and decisions about sport and exercise should consider enjoyment, readiness, supervision, and the advice of qualified coaches or clinicians.
                  </p>
                  
                  <p className="leading-relaxed">
                    <span className="font-medium text-text-primary">Safeguarding and Data:</span> This app stores information about a child. Use it only as a parent or guardian, keep login details private, and delete data you no longer need.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Data Privacy</h2>
            <ul className="space-y-3 text-text-secondary">
              <li className="flex items-start">
                <Icon name="Lock" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                <span>Only essential data required for growth tracking is stored</span>
              </li>
              <li className="flex items-start">
                <Icon name="Lock" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                <span>No location data is collected or stored</span>
              </li>
              <li className="flex items-start">
                <Icon name="Lock" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                <span>You can export your data to CSV at any time</span>
              </li>
              <li className="flex items-start">
                <Icon name="Lock" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                <span>You can delete child profiles and all measurements with a single click</span>
              </li>
              <li className="flex items-start">
                <Icon name="Lock" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                <span>Only you can view or edit your child's data</span>
              </li>
            </ul>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <Input
              label="Parent/Guardian Name"
              type="text"
              value={parentName}
              onChange={(e) => setParentName(e?.target?.value)}
              placeholder="Enter your full name"
              required
            />
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e?.target?.checked)}
                className="mt-1 mr-3 h-5 w-5 rounded border-input text-primary focus:ring-2 focus:ring-primary"
              />
              <span className="text-text-primary">
                I acknowledge that I have read and understood the above responsibilities and privacy information. 
                I consent to using this application to track my child's growth data.
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-between">
          <Button 
            onClick={onDecline}
            variant="outline"
            iconName="X"
          >
            Decline
          </Button>
          <Button 
            onClick={handleAccept}
            disabled={!acknowledged || !parentName?.trim()}
            iconName="Check"
          >
            Accept and Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConsentSection;